import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ExternalLink, Network } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import OrgChart from '../AccountWorkspace/OrgChart';
import AddContactModal from '../AccountWorkspace/AddContactModal';
import ContactDetailModal from '../AccountWorkspace/ContactDetailModal';
// The OrgChart wrapper's chrome (trash tray, wrap layout) lives in the
// workspace stylesheet — classes are aw- prefixed so this import is safe.
import '../AccountWorkspace/AccountWorkspace.css';

/*
 * AccountsPeopleTabs — Org chart + Contacts tabs for /accounts.
 * Both read the SAME /api/workspace aggregation (same queryKey) that
 * AccountWorkspace uses, so the org chart here is a real mount of the
 * existing component, not a copy — mutations invalidate the shared
 * ['workspace'] cache and both pages stay consistent.
 */

/* Shared by the people tabs and the Meetings tab. Same react-query key the
   AccountWorkspace uses, so the payload is fetched once and reused rather
   than each tab re-resolving the account. */
export function useWorkspaceData(selectedDepartment) {
  const deptKey = selectedDepartment?.acronym || selectedDepartment?.official_name || null;
  return useQuery({
    queryKey: ['workspace', selectedDepartment?.department_id, deptKey],
    queryFn: () => apiClient.get(
      `/api/workspace?department_id=${selectedDepartment?.department_id || ''}` +
      `&label=${encodeURIComponent(selectedDepartment?.acronym || '')}` +
      `&name=${encodeURIComponent(selectedDepartment?.official_name || '')}`
    ),
    enabled: !!deptKey,
    staleTime: 60_000,
  });
}

/**
 * AccountsPeopleTab — org chart and contact list in ONE tab.
 *
 * GAS keeps people under a single Org Chart tab; TekStak had split them into
 * Org chart and Contacts, which put the same roster behind two clicks and made
 * the list a dead end (Ryan, 2026-09-09: "I like the org chart being part of
 * the accounts page like GAS, not split"). Same view toggle the workspace uses,
 * one workspace fetch behind both, and the list now opens the contact modal
 * instead of being inert.
 */
export function AccountsPeopleTab({
  selectedDepartment,
  compact = false,
  view: lockedView = null,
  initialView = null,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Two different props, because they answer two different questions.
  // `view` LOCKS one and hides the switch — the old board behaviour, which is
  // what made 'org-chart' and 'contacts' the same widget twice.
  // `initialView` only picks which one opens FIRST and leaves the switch in
  // place. That is what the People widget passes, so a rep whose board carried
  // the flat-list widget still opens on the list after the merge, and can now
  // reach the chart without adding a second card.
  const [ownView, setView] = useState(initialView || 'chart');
  const view = lockedView || ownView;
  const { data, isLoading, error } = useWorkspaceData(selectedDepartment);
  // Same add/edit modals as the workspace, so the org chart here is the
  // full thing (GAS parity), not a read-mostly preview.
  const [addManager, setAddManager] = useState(undefined); // undefined = closed
  const [selectedContact, setSelectedContact] = useState(null);

  if (isLoading) return <div className="accounts-panel-note">Loading people…</div>;
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load contacts'}
      </div>
    );
  }

  const contacts = data?.contacts || [];
  const accountId = data?.account_id || null;

  return (
    <div className="ts-card accounts-org-card">
      {/* On the board the widget shell already carries the title and the
          arrow into this tab, so rendering them again gives two competing
          headers and two "open full view" affordances.
          NOT `hidden={compact}`: .accounts-card-head is display:flex, which
          beats the UA stylesheet's [hidden]{display:none}, so the attribute
          silently did nothing. */}
      {!compact && (
      <div className="accounts-card-head">
        <h3 className="ts-card-title">
          <Network size={14} /> People
          <span className="accounts-eyebrow">{contacts.length} contacts</span>
        </h3>
        {!lockedView && (
          <div className="accounts-view-toggle" role="tablist" aria-label="People view">
            <button
              role="tab"
              aria-selected={view === 'chart'}
              className={view === 'chart' ? 'is-active' : ''}
              onClick={() => setView('chart')}
            >
              Org chart
            </button>
            <button
              role="tab"
              aria-selected={view === 'list'}
              className={view === 'list' ? 'is-active' : ''}
              onClick={() => setView('list')}
            >
              List
            </button>
          </div>
        )}
        <button
          className="accounts-link-btn"
          onClick={() => navigate(`/workspace?view=${view}`)}
        >
          Open full view <ExternalLink size={12} />
        </button>
      </div>
      )}

      {view === 'list' && (
        <div className="accounts-contact-list">
          {contacts.length === 0 && (
            <p className="accounts-panel-note">
              No contacts recorded yet — mention people in Sales AI or add them here.
            </p>
          )}
          {contacts.map((c) => {
            const fullName = `${c.first_name} ${c.last_name || ''}`.trim();
            return (
              <button
                type="button"
                key={c.id}
                className="accounts-contact accounts-contact--clickable"
                onClick={() => setSelectedContact(c)}
              >
                <div className="accounts-contact-head">
                  <span className="accounts-contact-name">{fullName}</span>
                  <span className="accounts-contact-chips">
                    {c.influence_level && c.influence_level !== 'unknown' && (
                      <span className="accounts-chip">{c.influence_level.replace('_', ' ')}</span>
                    )}
                    {c.relationship_status && (
                      <span className="accounts-chip">{c.relationship_status}</span>
                    )}
                  </span>
                </div>
                <span className="accounts-contact-title">
                  {c.title || 'Title unknown'}
                  {c.reports_to && <span> · reports to {c.reports_to}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="accounts-org-canvas" hidden={view !== 'chart'}>
        <OrgChart
          contacts={contacts}
          accountId={accountId}
          departmentId={selectedDepartment?.department_id}
          departmentLabel={selectedDepartment?.acronym || selectedDepartment?.official_name}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
          onAddContact={(manager) => setAddManager(manager || null)}
          onContactClick={(contact) => setSelectedContact(contact)}
        />
      </div>
      {addManager !== undefined && (
        <AddContactModal
          onClose={() => setAddManager(undefined)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
          contacts={contacts}
          accountId={accountId}
          departmentId={selectedDepartment?.department_id}
          departmentLabel={selectedDepartment?.acronym || selectedDepartment?.official_name}
          preselectedManager={addManager}
        />
      )}
      {selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
          allContacts={contacts}
        />
      )}
    </div>
  );
}
